import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface CollapsibleSidePanelProps {
  collapsed: boolean
  onToggle: () => void
  darkMode?: boolean
  icon: React.ReactNode
  collapseLabel: string
  expandLabel: string
  children: React.ReactNode
}

/**
 * Left workspace panel that collapses to a slim 40px rail. The rail keeps
 * the panel icon and an expand chevron so the user can always bring it back.
 * Width follows the CMA pattern: clamp(350px, 26vw, 500px).
 */
const CollapsibleSidePanel: React.FC<CollapsibleSidePanelProps> = ({
  collapsed,
  onToggle,
  darkMode = false,
  icon,
  collapseLabel,
  expandLabel,
  children
}) => {
  const border = darkMode ? 'border-gray-700' : 'border-gray-200'
  const surface = darkMode ? 'bg-gray-900' : 'bg-white'

  if (collapsed) {
    return (
      <aside className={`hidden md:flex w-10 shrink-0 flex-col items-center gap-2 border-r pt-3 ${surface} ${border}`}>
        <button
          onClick={onToggle}
          className={`flex flex-col items-center gap-2 rounded-md p-1.5 transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
          title={expandLabel}
          aria-label={expandLabel}
          aria-expanded={false}
        >
          {icon}
          <ChevronRight size={16} />
        </button>
      </aside>
    )
  }

  return (
    <aside className={`hidden md:flex w-[clamp(350px,26vw,500px)] shrink-0 min-h-0 min-w-0 border-r ${surface} ${border}`}>
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
      <div className={`flex w-6 shrink-0 items-start justify-center border-l pt-3 ${border}`}>
        <button
          onClick={onToggle}
          className={`rounded p-0.5 transition-colors ${darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
          title={collapseLabel}
          aria-label={collapseLabel}
          aria-expanded={true}
        >
          <ChevronLeft size={16} />
        </button>
      </div>
    </aside>
  )
}

export default CollapsibleSidePanel
